/**
 * Gestione del limite dei prodotti campione nel carrello
 * Impedisce di superare il limite di 3 pezzi per sessione
 */

class CartSampleLimit {
  constructor() {
    this.maxQuantityPerSession = 3;
    this.sessionStorageKey = 'sample_products_session';
    this.firstVariantItems = {}; // Mappa degli item che sono prima variante
    this.sampleProducts = {}; // Info sui prodotti campione
    this.init();
  }

  init() {
    console.log('Inizializzazione CartSampleLimit...');
    // Reset delle mappe
    this.firstVariantItems = {};
    this.sampleProducts = {};
    
    this.identifySampleProducts();
    this.bindEvents();
    this.setupMutationObserver();
    this.checkAndUpdateLimits();
    
    console.log('Prime varianti identificate:', this.firstVariantItems);
    console.log('Altri campioni identificati:', this.sampleProducts);
  }
  
  /**
   * Configura un observer per rilevare modifiche al carrello
   */
  setupMutationObserver() {
    // Disconnetti observer esistente se presente
    if (this.cartObserver) {
      this.cartObserver.disconnect();
    }
    
    // Target: il contenitore del carrello
    const cartContainer = document.querySelector('.cart__items, .cart-drawer__items, form[action*="/cart"]');
    if (!cartContainer) return;
    
    // Crea un nuovo observer
    this.cartObserver = new MutationObserver((mutations) => {
      // Controlla se ci sono stati cambiamenti significativi
      let shouldReinit = false;
      
      mutations.forEach(mutation => {
        // Controlla se sono stati rimossi nodi
        if (mutation.removedNodes.length > 0) {
          mutation.removedNodes.forEach(node => {
            if (node.nodeType === 1 && (node.classList?.contains('cart__item') || node.dataset?.item)) {
              console.log('Item rimosso dal carrello, reinizializzazione...');
              shouldReinit = true;
            }
          });
        }
        
        // Controlla se sono stati aggiunti nodi
        if (mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1 && (node.classList?.contains('cart__item') || node.dataset?.item)) {
              console.log('Item aggiunto al carrello, reinizializzazione...');
              shouldReinit = true;
            }
          });
        }
      });
      
      if (shouldReinit) {
        // Usa un debounce per evitare reinizializzazioni multiple
        clearTimeout(this.reinitTimeout);
        this.reinitTimeout = setTimeout(() => {
          console.log('Reinizializzazione dopo modifica carrello...');
          this.init();
        }, 300);
      }
    });
    
    // Configura e avvia l'observer
    this.cartObserver.observe(cartContainer, {
      childList: true,
      subtree: true,
      attributes: false
    });
  }

  /**
   * Identifica i prodotti campione nel carrello e determina quali sono prima variante
   */
  async identifySampleProducts() {
    // Cerca tutti gli elementi del carrello che hanno proprietà indicanti che sono campioni
    const cartItems = document.querySelectorAll('.cart__item, [data-item]');
    
    for (const item of cartItems) {
      const properties = item.querySelectorAll('.cart__item__property');
      let isSample = false;
      let sampleProductTitle = null;
      let variantTitle = null;
      
      let isFirstVariantByProperty = false;
      
      properties.forEach(prop => {
        const propertyText = prop.textContent || '';
        const strongText = prop.querySelector('strong')?.textContent || '';
        const spanText = prop.querySelector('span')?.textContent || '';
        
        console.log('Controllo proprietà:', propertyText);
        
        // Controlla se il prodotto ha la proprietà "Prodotto campione"
        if (propertyText.includes('Prodotto campione') || strongText.includes('Prodotto campione')) {
          isSample = true;
          // Estrai il nome del prodotto campione
          sampleProductTitle = spanText || propertyText.replace('Prodotto campione:', '').replace('Prodotto campione', '').trim();
        }
        if (propertyText.includes('Variante selezionata') || strongText.includes('Variante selezionata')) {
          // Estrai il nome della variante
          variantTitle = spanText || propertyText.replace('Variante selezionata:', '').replace('Variante selezionata', '').trim();
        }
        if (propertyText.includes('Gratuita') && (propertyText.includes('Sì') || spanText === 'Sì')) {
          // Questo è il modo più sicuro per identificare la prima variante gratuita
          isFirstVariantByProperty = true;
          console.log('PRIMA VARIANTE GRATUITA identificata tramite proprietà!');
        }
        if (propertyText.includes('Prodotto di riferimento') || strongText.includes('Prodotto di riferimento')) {
          isSample = true;
        }
      });
      
      if (isSample) {
        const itemKey = item.dataset.item;
        
        // Usa la proprietà se disponibile, altrimenti usa l'euristica
        let isFirstVariant = isFirstVariantByProperty;
        
        if (!isFirstVariantByProperty) {
          // Fallback: determina se questa è la prima variante usando l'euristica
          const variantOptions = item.querySelectorAll('.cart__item__selected-options');
          
          // Se non ci sono opzioni variante o è "Default Title", è probabilmente la prima
          if (variantOptions.length === 0 || variantTitle === 'Default Title' || variantTitle === 'Titolo predefinito') {
            isFirstVariant = true;
          } else {
            // Controlla se tutti i valori delle opzioni sono i primi disponibili
            const optionValues = Array.from(variantOptions).map(opt => {
              const value = opt.querySelector('span')?.textContent?.trim();
              return value;
            });
            
            // Se tutte le opzioni sono le prime (assumendo ordine alfabetico/numerico)
            isFirstVariant = optionValues.every(val => {
              // Controlla se è il primo valore tipico
              return val === 'XS' || val === 'S' || val === 'Small' || 
                     val === '1' || val === 'Default' || !val;
            });
          }
        }
        
        if (isFirstVariant) {
          this.firstVariantItems[itemKey] = {
            productTitle: sampleProductTitle,
            variantTitle: variantTitle,
            isFirstVariant: true
          };
          console.log('Prima variante campione identificata:', itemKey, sampleProductTitle, variantTitle);
        } else {
          this.sampleProducts[itemKey] = {
            productTitle: sampleProductTitle,
            variantTitle: variantTitle,
            isFirstVariant: false
          };
          console.log('Altra variante campione identificata:', itemKey, sampleProductTitle, variantTitle);
        }
      }
    }
  }

  /**
   * Bind degli eventi per i controlli quantità
   */
  bindEvents() {
    console.log('Binding eventi per controllo quantità...');
    
    // Intercetta tutti i click sui pulsanti quantità PRIMA che vengano processati
    document.addEventListener('click', (e) => {
      const plusButton = e.target.closest('.cart__quantity-plus');
      const minusButton = e.target.closest('.cart__quantity-minus');
      
      if (plusButton || minusButton) {
        const button = plusButton || minusButton;
        const quantityWrapper = button.closest('.cart__quantity, .cart__quantity-counter, quantity-counter');
        
        if (!quantityWrapper) {
          console.log('Wrapper quantità non trovato');
          return;
        }
        
        const input = quantityWrapper.querySelector('.cart__quantity-field, input[type="number"]');
        if (!input) {
          console.log('Input quantità non trovato');
          return;
        }
        
        const itemKey = input.dataset.id || input.name?.replace('updates[]', '');
        console.log('Click su pulsante quantità, itemKey:', itemKey);
        
        // Controllo solo per la prima variante
        if (this.isFirstVariantSample(itemKey)) {
          console.log('È prima variante, controllo limite...');
          const isIncrease = !!plusButton;
          
          if (isIncrease) {
            const currentValue = parseInt(input.value) || 0;
            if (!this.canIncreaseQuantity(itemKey, currentValue)) {
              console.log('BLOCCO incremento - limite raggiunto');
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              this.showLimitMessage(button.closest('.cart__item, [data-item]'));
              return false;
            }
          }
        }
      }
    }, true); // Usa capture phase per intercettare prima

    // Eventi per input diretti nel campo quantità
    document.addEventListener('change', (e) => {
      const input = e.target;
      if (input.classList.contains('cart__quantity-field') || input.type === 'number') {
        const itemKey = input.dataset.id || input.name?.replace('updates[]', '');
        
        console.log('Change su input quantità, itemKey:', itemKey);
        
        // Controllo solo per la prima variante
        if (this.isFirstVariantSample(itemKey)) {
          console.log('Validazione input per prima variante');
          this.validateQuantityInput(input, itemKey);
        }
      }
    }, true);
    
    // Intercetta anche l'evento input per validazione in tempo reale
    document.addEventListener('input', (e) => {
      const input = e.target;
      if (input.classList.contains('cart__quantity-field') || input.type === 'number') {
        const itemKey = input.dataset.id || input.name?.replace('updates[]', '');
        
        // Controllo solo per la prima variante
        if (this.isFirstVariantSample(itemKey)) {
          const newValue = parseInt(input.value) || 0;
          const maxAllowed = this.getMaxAllowedQuantity(itemKey);
          
          if (newValue > maxAllowed) {
            console.log('Input supera il limite, correzione a:', maxAllowed);
            input.value = maxAllowed;
            e.preventDefault();
            this.showLimitMessage(input.closest('.cart__item, [data-item]'));
          }
        }
      }
    }, true);

    // Intercetta il submit del form del carrello
    const cartForm = document.querySelector('form[action*="/cart"]');
    if (cartForm) {
      cartForm.addEventListener('submit', (e) => {
        if (!this.validateAllSampleQuantities()) {
          e.preventDefault();
          this.showGlobalError();
        }
      });
    }
  }

  /**
   * Verifica se un item è la prima variante di un prodotto campione
   */
  isFirstVariantSample(itemKey) {
    return this.firstVariantItems.hasOwnProperty(itemKey);
  }
  
  /**
   * Verifica se un item è un prodotto campione (qualsiasi variante)
   */
  isSampleProduct(itemKey) {
    return this.firstVariantItems.hasOwnProperty(itemKey) || this.sampleProducts.hasOwnProperty(itemKey);
  }

  /**
   * Gestisce il cambio di quantità tramite pulsanti
   */
  handleQuantityChange(button, input, itemKey) {
    const isIncrease = button.classList.contains('cart__quantity-plus');
    const currentValue = parseInt(input.value) || 0;
    const newValue = isIncrease ? currentValue + 1 : currentValue - 1;
    
    if (isIncrease && !this.canIncreaseQuantity(itemKey, currentValue)) {
      // Blocca l'incremento
      this.showLimitMessage(button.closest('.cart__item'));
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  }

  /**
   * Valida l'input diretto nel campo quantità
   */
  validateQuantityInput(input, itemKey) {
    const newValue = parseInt(input.value) || 0;
    const maxAllowed = this.getMaxAllowedQuantity(itemKey);
    
    if (newValue > maxAllowed) {
      input.value = maxAllowed;
      this.showLimitMessage(input.closest('.cart__item'));
      
      // Aggiorna il carrello con la quantità massima consentita
      this.updateCartQuantity(itemKey, maxAllowed);
    }
  }

  /**
   * Verifica se è possibile aumentare la quantità
   */
  canIncreaseQuantity(itemKey, currentQuantity) {
    // Controllo solo per le prime varianti
    if (!this.isFirstVariantSample(itemKey)) {
      return true; // Nessun limite per altre varianti
    }
    
    const totalInSession = this.getTotalFirstVariantQuantityInSession();
    const maxAllowed = this.maxQuantityPerSession;
    
    // Se stiamo già al limite, non permettere l'aumento
    if (totalInSession >= maxAllowed) {
      console.log('Limite sessione raggiunto per prima variante:', totalInSession, '/', maxAllowed);
      return false;
    }
    
    return true;
  }

  /**
   * Ottiene la quantità massima consentita per un item
   */
  getMaxAllowedQuantity(itemKey) {
    // Se non è prima variante, nessun limite
    if (!this.isFirstVariantSample(itemKey)) {
      return 999;
    }
    
    const totalInSession = this.getTotalFirstVariantQuantityInSession();
    const currentItemQuantity = this.getCurrentItemQuantity(itemKey);
    const otherItemsQuantity = totalInSession - currentItemQuantity;
    
    return this.maxQuantityPerSession - otherItemsQuantity;
  }

  /**
   * Ottiene la quantità corrente di un item nel carrello
   */
  getCurrentItemQuantity(itemKey) {
    const input = document.querySelector(`input[data-id="${itemKey}"]`);
    return input ? parseInt(input.value) || 0 : 0;
  }

  /**
   * Ottiene il totale delle PRIME VARIANTI dei campioni nella sessione
   */
  getTotalFirstVariantQuantityInSession() {
    let total = 0;
    
    // Somma solo le quantità delle prime varianti nel carrello
    Object.keys(this.firstVariantItems).forEach(itemKey => {
      total += this.getCurrentItemQuantity(itemKey);
    });
    
    return total;
  }

  /**
   * Ottiene i dati della sessione
   */
  getSessionData() {
    const data = sessionStorage.getItem(this.sessionStorageKey);
    return data ? JSON.parse(data) : {};
  }

  /**
   * Controlla e aggiorna i limiti all'inizializzazione
   */
  checkAndUpdateLimits() {
    // Prima pulisci tutti i messaggi e stati precedenti
    this.clearAllWarnings();
    this.resetAllButtons();
    
    const totalQuantity = this.getTotalFirstVariantQuantityInSession();
    
    console.log('Controllo limiti - Totale prime varianti:', totalQuantity);
    
    // Se non ci sono più prime varianti, pulisci tutto e esci
    if (totalQuantity === 0) {
      console.log('Nessuna prima variante nel carrello, pulizia completa');
      return;
    }
    
    // Controlla ogni item prima variante individualmente
    Object.keys(this.firstVariantItems).forEach(itemKey => {
      const currentQuantity = this.getCurrentItemQuantity(itemKey);
      const canIncrease = this.canIncreaseQuantity(itemKey, currentQuantity);
      
      console.log(`Item ${itemKey}: quantità=${currentQuantity}, può incrementare=${canIncrease}`);
      
      if (!canIncrease) {
        // Disabilita il pulsante + per questo item specifico
        this.disableIncreaseButtonForItem(itemKey);
      }
    });
    
    if (totalQuantity >= this.maxQuantityPerSession) {
      // Disabilita tutti i pulsanti di incremento per le prime varianti
      this.disableIncreaseButtons();
      this.showGlobalLimitWarning();
    } else if (totalQuantity >= this.maxQuantityPerSession - 1) {
      // Mostra avviso quando vicini al limite
      this.showGlobalLimitWarning();
    }
    
    // Aggiorna il messaggio di avviso
    this.updateWarningMessages();
  }
  
  /**
   * Pulisce tutti gli avvisi e messaggi di limite
   */
  clearAllWarnings() {
    // Rimuovi avvisi globali
    const globalWarnings = document.querySelectorAll('.cart-sample-warning');
    globalWarnings.forEach(warning => warning.remove());
    
    // Rimuovi messaggi di limite per singoli item
    const limitMessages = document.querySelectorAll('.sample-limit-message');
    limitMessages.forEach(message => message.remove());
    
    // Rimuovi badge campione
    const badges = document.querySelectorAll('.sample-badge');
    badges.forEach(badge => badge.remove());
    
    // Rimuovi classi is-sample-product
    const sampleItems = document.querySelectorAll('.is-sample-product');
    sampleItems.forEach(item => item.classList.remove('is-sample-product'));
  }
  
  /**
   * Ripristina lo stato di tutti i pulsanti
   */
  resetAllButtons() {
    // Ripristina tutti i pulsanti + che erano disabilitati
    const disabledButtons = document.querySelectorAll('.cart__quantity-plus[data-limit-reached="true"]');
    disabledButtons.forEach(button => {
      button.disabled = false;
      button.style.opacity = '';
      button.style.cursor = '';
      button.title = '';
      delete button.dataset.limitReached;
    });
  }
  
  /**
   * Disabilita il pulsante di incremento per un item specifico
   */
  disableIncreaseButtonForItem(itemKey) {
    const item = document.querySelector(`[data-item="${itemKey}"]`);
    if (item) {
      const plusButton = item.querySelector('.cart__quantity-plus');
      if (plusButton) {
        plusButton.disabled = true;
        plusButton.style.opacity = '0.3';
        plusButton.style.cursor = 'not-allowed';
        plusButton.title = 'Limite massimo raggiunto per questa variante';
        
        // Aggiungi anche un attributo data per identificarlo
        plusButton.dataset.limitReached = 'true';
      }
    }
  }

  /**
   * Disabilita i pulsanti di incremento per i prodotti campione
   */
  disableIncreaseButtons() {
    this.sampleProductHandles.forEach(itemKey => {
      const item = document.querySelector(`[data-item="${itemKey}"]`);
      if (item) {
        const plusButton = item.querySelector('.cart__quantity-plus');
        if (plusButton) {
          plusButton.disabled = true;
          plusButton.style.opacity = '0.5';
          plusButton.style.cursor = 'not-allowed';
        }
      }
    });
  }

  /**
   * Mostra un messaggio di limite per un item specifico
   */
  showLimitMessage(itemElement) {
    // Rimuovi messaggi esistenti
    const existingMessage = itemElement.querySelector('.sample-limit-message');
    if (existingMessage) {
      existingMessage.remove();
    }
    
    // Crea e inserisci il nuovo messaggio
    const message = document.createElement('div');
    message.className = 'sample-limit-message';
    message.innerHTML = `
      <p style="color: #d63384; font-size: 14px; margin-top: 10px; padding: 10px; background: #fff3f3; border-radius: 4px;">
        ⚠️ ${window.sampleTranslations?.limitReached || 'Hai raggiunto il limite massimo di 3 campioni per sessione.'}
      </p>
    `;
    
    const contentDiv = itemElement.querySelector('.cart__item__content');
    if (contentDiv) {
      contentDiv.appendChild(message);
      
      // Rimuovi il messaggio dopo 5 secondi
      setTimeout(() => {
        message.remove();
      }, 5000);
    }
  }

  /**
   * Mostra un avviso globale sul limite
   */
  showGlobalLimitWarning() {
    // Cerca o crea un contenitore per l'avviso globale
    let warningContainer = document.querySelector('.cart-sample-warning');
    
    if (!warningContainer) {
      warningContainer = document.createElement('div');
      warningContainer.className = 'cart-sample-warning';
      
      const cartForm = document.querySelector('form[action*="/cart"], .cart__inner, .cart-drawer__inner');
      if (cartForm) {
        cartForm.insertBefore(warningContainer, cartForm.firstChild);
      }
    }
    
    const totalQuantity = this.getTotalFirstVariantQuantityInSession();
    const remaining = this.maxQuantityPerSession - totalQuantity;
    
    let message = '';
    if (remaining <= 0) {
      message = window.sampleTranslations?.limitReached || 
                'Hai raggiunto il limite massimo di 3 campioni per questa sessione di acquisto.';
    } else {
      message = (window.sampleTranslations?.sessionLimit || 
                'Limite: massimo {max} campioni per sessione. Hai {current} nel carrello.')
                .replace('{max}', this.maxQuantityPerSession)
                .replace('{current}', totalQuantity);
    }
    
    warningContainer.innerHTML = `
      <div style="padding: 15px; background: #fff3cd; border: 1px solid #ffc107; border-radius: 6px; margin-bottom: 20px;">
        <p style="margin: 0; color: #856404; display: flex; align-items: center; gap: 10px;">
          <span style="font-size: 20px;">⚠️</span>
          <span>${message}</span>
        </p>
      </div>
    `;
  }

  /**
   * Aggiorna i messaggi di avviso
   */
  updateWarningMessages() {
    const totalQuantity = this.getTotalFirstVariantQuantityInSession();
    
    // Aggiungi indicatori visivi agli item campione
    this.sampleProductHandles.forEach(itemKey => {
      const item = document.querySelector(`[data-item="${itemKey}"]`);
      if (item && !item.classList.contains('is-sample-product')) {
        item.classList.add('is-sample-product');
        
        // Aggiungi un badge
        const titleElement = item.querySelector('.cart__item__title');
        if (titleElement && !titleElement.querySelector('.sample-badge')) {
          const badge = document.createElement('span');
          badge.className = 'sample-badge';
          badge.style.cssText = 'display: inline-block; margin-left: 8px; padding: 2px 8px; background: #17a2b8; color: white; font-size: 11px; border-radius: 3px; vertical-align: middle;';
          badge.textContent = 'Campione';
          titleElement.appendChild(badge);
        }
      }
    });
    
    // Mostra avviso se vicini al limite
    if (totalQuantity >= this.maxQuantityPerSession - 1) {
      this.showGlobalLimitWarning();
    }
  }

  /**
   * Valida tutte le quantità dei campioni
   */
  validateAllSampleQuantities() {
    const totalQuantity = this.getTotalFirstVariantQuantityInSession();
    return totalQuantity <= this.maxQuantityPerSession;
  }

  /**
   * Mostra errore globale
   */
  showGlobalError() {
    alert(window.sampleTranslations?.limitReached || 
          'Non puoi superare il limite di 3 campioni per sessione. Riduci la quantità dei campioni nel carrello.');
  }

  /**
   * Aggiorna la quantità nel carrello via AJAX
   */
  async updateCartQuantity(itemKey, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: itemKey,
          quantity: quantity
        })
      });
      
      if (response.ok) {
        // Ricarica la pagina o aggiorna il carrello
        if (window.location.pathname.includes('/cart')) {
          window.location.reload();
        } else {
          // Trigger evento per aggiornare il cart drawer
          document.dispatchEvent(new CustomEvent('cart:updated'));
        }
      }
    } catch (error) {
      console.error('Errore aggiornamento quantità:', error);
    }
  }
}

// Inizializza quando il DOM è pronto
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    setTimeout(() => {
      window.cartSampleLimit = new CartSampleLimit();
    }, 500);
  });
} else {
  setTimeout(() => {
    window.cartSampleLimit = new CartSampleLimit();
  }, 500);
}

// Reinizializza quando il carrello viene aggiornato dinamicamente
document.addEventListener('cart:updated', () => {
  setTimeout(() => {
    if (window.cartSampleLimit) {
      window.cartSampleLimit.init();
    } else {
      window.cartSampleLimit = new CartSampleLimit();
    }
  }, 100);
});

// Reinizializza per cart drawer
document.addEventListener('cart-drawer:opened', () => {
  setTimeout(() => {
    if (window.cartSampleLimit) {
      window.cartSampleLimit.init();
    } else {
      window.cartSampleLimit = new CartSampleLimit();
    }
  }, 100);
});

// Ascolta gli eventi Shopify del carrello
document.addEventListener('cart:change', () => {
  setTimeout(() => {
    if (window.cartSampleLimit) {
      window.cartSampleLimit.init();
    }
  }, 100);
});

// Intercetta le chiamate AJAX al carrello per rilevare rimozioni
(function() {
  const originalFetch = window.fetch;
  window.fetch = function(...args) {
    const [url, options] = args;
    
    // Controlla se è una chiamata di rimozione dal carrello
    if (typeof url === 'string' && (url.includes('/cart/change') || url.includes('/cart/update'))) {
      return originalFetch.apply(this, args).then(response => {
        // Dopo una modifica al carrello, reinizializza
        setTimeout(() => {
          if (window.cartSampleLimit) {
            console.log('Rilevata modifica carrello via AJAX, reinizializzazione...');
            window.cartSampleLimit.init();
          }
        }, 300);
        return response;
      });
    }
    
    return originalFetch.apply(this, args);
  };
})();